/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { ManageRefreshmentsModalProvider } from './context'
import { Modal } from 'antd'
import colors from '@sportninja/common/constants/appColors'
import React, { useCallback, useMemo } from 'react'
import { CloseOutlined } from '@ant-design/icons'
import ManageRefreshmentsModalContent from './ManageRefreshmentsModalContent'
import useGamePlayerRoster from '@sportninja/common/hooks/useGamePlayerRoster'
import ModalHeader from './components/ModalHeader'
import ModalFooter from './components/ModalFooter'

const modalStyles = {
  header: {
    backgroundColor: colors.SECONDARY_300,
    paddingLeft: '16px',
    paddingRight: '16px',
  },
  body: {
    borderBottom: `1px solid ${colors.SOFT_STEEL}`,
    borderTop: `1px solid ${colors.SOFT_STEEL}`,
    backgroundColor: colors.SECONDARY_300,
  },
  content: {
    backgroundColor: colors.SECONDARY_300,
    paddingTop: '8px',
    paddingBottom: '16px',
    paddingLeft: '0',
    paddingRight: '0',
  },
  wrapper: {
    zIndex: 10001,
  },
  mask: {
    backdropFilter: 'blur(2px)',
    backgroundColor: 'rgba(6, 10, 16, 0.8)',
    zIndex: 10000,
  },
  footer: {
    paddingLeft: '16px',
    paddingRight: '16px',
  },
}

type ManageRefreshmentsModalProps = {
  isManageRefreshmentsModalOpen: boolean
  setIsManageRefreshmentsModalOpen: (isOpen: boolean) => void
  selectedManageRefreshments: {
    gameId: string
    rosterId: string
    teamId: string
    teamName: string
    showWaiver: boolean
  } | null
  readAttendanceInformation: () => void
}

/** Inner component that has access to context for footer/header rendering */
const ManageRefreshmentsModalInner: React.FC<{
  isOpen: boolean
  onClose: () => void
  selectedManageRefreshments: ManageRefreshmentsModalProps['selectedManageRefreshments']
  readAttendanceInformation: () => void
}> = ({
  isOpen,
  onClose,
  selectedManageRefreshments,
  readAttendanceInformation,
}) => {
  const { roster, officials } = useGamePlayerRoster(
    selectedManageRefreshments?.gameId,
    selectedManageRefreshments?.rosterId
  )

  const mergedRoster = useMemo(() => {
    if (!roster || !officials) {
      return []
    }
    return [...roster.players, ...officials.team_officials]
  }, [roster, officials])

  const closeIcon = useCallback(() => {
    return (
      <CloseOutlined
        css={css`
          color: ${colors.SOLITUDE};
          font-size: 16px;
        `}
      />
    )
  }, [])

  return (
    <Modal
      open={isOpen}
      onOk={onClose}
      onCancel={onClose}
      css={css`
        .ant-modal-wrap {
          z-index: 10001;
        }
      `}
      width={600}
      centered
      styles={modalStyles}
      maskClosable={false}
      footer={<ModalFooter onClose={onClose} />}
      title={<ModalHeader />}
      closeIcon={closeIcon()}
    >
      <ManageRefreshmentsModalContent
        players={mergedRoster || []}
        gameId={roster?.game_id}
        rosterId={roster?.id}
        setIsManageRefreshmentsModalOpen={onClose}
        readAttendanceInformation={readAttendanceInformation}
      />
    </Modal>
  )
}

const ManageRefreshmentsModal: React.FC<ManageRefreshmentsModalProps> = ({
  isManageRefreshmentsModalOpen,
  setIsManageRefreshmentsModalOpen,
  selectedManageRefreshments,
  readAttendanceInformation = () => {},
}) => {
  const handleClose = useCallback(() => {
    setIsManageRefreshmentsModalOpen(false)
  }, [setIsManageRefreshmentsModalOpen])

  if (!isManageRefreshmentsModalOpen) {
    return null
  }

  return (
    <ManageRefreshmentsModalProvider>
      <ManageRefreshmentsModalInner
        isOpen={isManageRefreshmentsModalOpen}
        onClose={handleClose}
        selectedManageRefreshments={selectedManageRefreshments}
        readAttendanceInformation={readAttendanceInformation}
      />
    </ManageRefreshmentsModalProvider>
  )
}

export default ManageRefreshmentsModal
